import React, { useEffect, useRef, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import "./responsive-navbar.css";

function Navbar() {
  const navigate = useNavigate();
  const [dashboardPath, setDashboardPath] = useState("/");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ruolo, setRuolo] = useState(null);
  const [infoLocale, setInfoLocale] = useState({});
  const navEl = useRef(null);

  // 🔥 NAVBAR HEIGHT
  useEffect(() => {
    const updateNavbarHeight = () => {
      if (!navEl.current) return;

      const height = navEl.current.offsetHeight;
      document.documentElement.style.setProperty("--navbar-height", height + "px");
      document.documentElement.style.setProperty("--navbar-height-mobile", height + "px");

      const safeAreaTop = window.visualViewport?.offsetTop || 0;
      document.documentElement.style.setProperty("--safe-area-top", safeAreaTop + "px");
    };

    updateNavbarHeight();
    window.addEventListener("resize", updateNavbarHeight);
    return () => window.removeEventListener("resize", updateNavbarHeight);
  }, []);

  // 🔥 USER + LOCALE REALTIME
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    let unsubscribe = null;

    const init = async () => {
      const userRef = doc(db, "utenti", user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) return;

      const data = snap.data();
      const ruoloUtente = data.ruolo?.toLowerCase()?.trim();
      const localeTag = data.localeTag || data.locali?.[0];

      setRuolo(ruoloUtente);

      const routes = {
        admin: "/dashboard-admin",
        gestore: "/dashboard-gestore",
        cassa: "/dashboard-cassa",
      };

      setDashboardPath(routes[ruoloUtente] || "/");

      if (localeTag) {
        const ref = doc(db, "locali", localeTag);
        unsubscribe = onSnapshot(ref, (snap) => {
          if (!snap.exists()) return;
          setInfoLocale(snap.data());
        });
      }
    };

    init();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleBack = () => navigate(dashboardPath);

  const handleLogout = async () => {
    await signOut(getAuth());
    navigate("/login");
  };

  return (
    <nav ref={navEl} className="navbar-responsive">
      {/* LOGO */}
      <div className="navbar-brand" onClick={handleBack}>
        <img src="/logo-menuvivo.png" alt="Logo" className="navbar-logo" />
      </div>

      {/* HAMBURGER */}
      <button className="navbar-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        <span className={`hamburger-line ${mobileMenuOpen ? "open" : ""}`} />
        <span className={`hamburger-line ${mobileMenuOpen ? "open" : ""}`} />
        <span className={`hamburger-line ${mobileMenuOpen ? "open" : ""}`} />
      </button>

      {/* MENU MOBILE */}
      {mobileMenuOpen && (
        <div className="navbar-mobile-menu">

          <button onClick={() => { handleBack(); setMobileMenuOpen(false); }} className="navbar-btn navbar-btn-secondary navbar-mobile-item">
            Dashboard
          </button>

          {/* ADMIN */}
          {ruolo === "admin" && (
            <>
              <button onClick={() => { navigate("/adminlocali"); setMobileMenuOpen(false); }} className="navbar-btn navbar-mobile-item">
                ➕ Crea locale
              </button>
              <button onClick={() => { navigate("/visualizzalocali"); setMobileMenuOpen(false); }} className="navbar-btn navbar-mobile-item">
                📋 Locali
              </button>
            </>
          )}

          {/* GESTORE */}
          {ruolo === "gestore" && (
            <>
              {!infoLocale.nascondiCamerieri && (
                <button onClick={() => { navigate("/gestione-camerieri"); setMobileMenuOpen(false); }} className="navbar-btn navbar-mobile-item">
                  👨‍🍳 Camerieri
                </button>
              )}

              {!infoLocale.nascondiBannerPromozionali && (
                <button onClick={() => { navigate("/gestione-banner-promozionale"); setMobileMenuOpen(false); }} className="navbar-btn navbar-mobile-item">
                  🎨 Comunicazione visiva
                </button>
              )}

              {!infoLocale.nascondiPromozioni && (
                <button onClick={() => { navigate("/gestione-promozioni"); setMobileMenuOpen(false); }} className="navbar-btn navbar-mobile-item">
                  🎉 Piatti in evidenza
                </button>
              )}

              {!infoLocale.nascondiReport && (
                <button onClick={() => { navigate("/reportgestore"); setMobileMenuOpen(false); }} className="navbar-btn navbar-mobile-item">
                  📊 Analisi performance
                </button>
              )}
            </>
          )}

          {/* CASSA */}
          {ruolo === "cassa" && (
            <button onClick={() => { navigate("/dashboard-cassa"); setMobileMenuOpen(false); }} className="navbar-btn navbar-mobile-item">
              💰 Dashboard Cassa
            </button>
          )}

          <button onClick={handleLogout} className="navbar-btn navbar-btn-danger navbar-mobile-item">
            Logout
          </button>

        </div>
      )}
    </nav>
  );
}

export default Navbar;